import { v4 as uuidv4 } from 'uuid';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import {
  getTextBlockHeight,
  fixImageOrientation,
  placeMetadataOnPage
} from './PDFUtils';

export function GeneratePdfHandler({ data }) {
  return async () => {
    const { documents, template, title, description, metadataPlacement } = data;
    const fileId = uuidv4();

    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();

    doc.setFont('helvetica', 'bold');
    const titleText = title || 'Untitled Report';
    const titleY = 20;
    doc.setFontSize(18);
    const titleWrapped = doc.splitTextToSize(titleText, pageWidth - 10);
    doc.text(titleWrapped, pageWidth / 2, titleY, { align: 'center' });

    const descriptionText = description || '';
    const descriptionWrapped = doc.splitTextToSize(descriptionText, pageWidth - 10);
    const descriptionY = getTextBlockHeight(doc, titleText, 18, pageWidth).height + titleY + 10;
    doc.setFontSize(10);
    doc.text(descriptionWrapped, pageWidth / 2, descriptionY, { align: 'center' });

    const headers = template.map(field => field.label);
    const rows = documents.map(docItem => (
      template.map(field => docItem.metadata?.[field.label] || '')
    ));

    const summableFields = template.filter(field => field.sum);
    const totals = {};
    for (const field of summableFields) {
      totals[field.label] = documents.reduce((acc, doc) => {
        const value = parseFloat(doc.metadata?.[field.label]);
        return acc + (isNaN(value) ? 0 : value);
      }, 0);
    }
    if (summableFields.length > 0) {
      const totalsRow = template.map(field => field.sum ? totals[field.label] : '');
      rows.push(totalsRow);
    }

    const tableY = getTextBlockHeight(doc, descriptionText, 10, pageWidth).height + descriptionY + 10;

    autoTable(doc, {
      startY: tableY,
      head: [headers],
      body: rows,
      margin: { left: 10, right: 10 },
      styles: { fontSize: 10 },
      didDrawCell: ({ row, doc }) => {
        doc.setFont(row.index === documents.length ? 'helvetica' : 'helvetica', row.index === documents.length ? 'bold' : 'normal');
      }
    });

    const jsPdfBuffer = doc.output('arraybuffer');
    const coverPdf = await PDFDocument.load(jsPdfBuffer);
    const mergedPdf = await PDFDocument.create();
    const coverPages = await mergedPdf.copyPages(coverPdf, coverPdf.getPageIndices());
    coverPages.forEach(page => mergedPdf.addPage(page));

    for (const docItem of documents) {
      const metaText = template.map(f => `${f.label}: ${docItem.metadata?.[f.label] || ''}`).join(' | ');

      if (docItem.file?.type === 'application/pdf') {
        const buffer = await docItem.file.arrayBuffer();
        const pdf = await PDFDocument.load(buffer);
        const pages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
        pages.forEach((page, idx) => {
          if (metadataPlacement === 'top') {
            placeMetadataOnPage(page, metaText);
          }
          mergedPdf.addPage(page);
        });
      } else if (docItem.file?.type?.startsWith('image/')) {
        const dataURL = await fixImageOrientation(docItem.file);
        const imgBytes = await fetch(dataURL).then(res => res.arrayBuffer());
        const image = await mergedPdf.embedJpg(imgBytes);
        const page = mergedPdf.addPage();
        const { width, height } = page.getSize();
        const dims = image.scaleToFit(width - 100, height - 100);
        const x = (width - dims.width) / 2;
        const y = (height - dims.height) / 2;
        page.drawImage(image, { x, y, width: dims.width, height: dims.height });
        page.drawText(`${image.width} x ${image.height}`, {
          x: width / 2 - 20,
          y: y - 15,
          size: 10,
          color: rgb(0, 0, 0)
        });
        if (metadataPlacement === 'top') {
          placeMetadataOnPage(page, metaText);
        }
      }
    }

    // --- Add "y of x" page numbers (bottom-right) ---
    const pageCount = mergedPdf.getPageCount();
    const font = await mergedPdf.embedFont(StandardFonts.Helvetica);
    const fontSize = 9;
    const margin = 16; // distance from page edge

    for (let i = 0; i < pageCount; i++) {
      const page = mergedPdf.getPage(i);
      const { width } = page.getSize();
      const text = `${i + 1} of ${pageCount}`;
      const textWidth = font.widthOfTextAtSize(text, fontSize);

      page.drawText(text, {
        x: width - margin - textWidth,
        y: margin,
        size: fontSize,
        font,
        color: rgb(0, 0, 0),
      });
    }

    const stampedPdfBytes = await mergedPdf.save();
    return { stampedPdfBytes, fileId };

  };
}